import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import './Financials.css';

const TABS = ['Cash Flow', 'Trial Balance', 'Aged Receivables', 'Aged Payables'];

export default function Financials() {
  const [tab, setTab] = useState('Cash Flow');

  return (
    <div className="fn-page">
      <div className="page-header">
        <h2 className="page-title">Financial Statements</h2>
      </div>

      <div className="fn-tabs">
        {TABS.map(t => (
          <button key={t} className={`fn-tab ${tab === t ? 'active' : ''}`}
                  onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'Cash Flow'        && <CashFlow />}
      {tab === 'Trial Balance'    && <TrialBalance />}
      {tab === 'Aged Receivables' && <AgedReceivables />}
      {tab === 'Aged Payables'    && <AgedPayables />}
    </div>
  );
}

const inr = (n) => '₹ ' + fmtNum(n);


/* ═══════════════════════════════════════════════════════════
   1. CASH FLOW
═══════════════════════════════════════════════════════════ */
function CashFlow() {
  const [from, setFrom] = useState('');
  const [to, setTo]     = useState(today());

  React.useEffect(() => {
    const d = new Date();
    setFrom(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10));
  }, []);

  const { data, loading, error, reload } = useApi(
    () => from && to ? api.financialsPlus.cashFlow(from, to) : Promise.resolve(null),
    [from, to]
  );

  return (
    <div className="fn-body">
      <div className="fn-filters">
        <div className="form-group">
          <label>From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={reload}>Refresh</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading || !data ? <Spinner /> : (
        <>
          <div className="fn-kpis">
            <Kpi label="Opening Cash"     value={inr(data.opening_cash)} />
            <Kpi label="Net from Operating" value={inr(data.operating?.net)} tone={data.operating?.net >= 0 ? 'green' : 'red'} />
            <Kpi label="Net Change"       value={inr(data.net_change)} tone={data.net_change >= 0 ? 'green' : 'red'} />
            <Kpi label="Closing Cash"     value={inr(data.closing_cash)} tone="blue" />
          </div>

          <ActivitySection title="Operating Activities" data={data.operating} colorClass="fn-op" />
          <ActivitySection title="Investing Activities" data={data.investing} colorClass="fn-inv" empty="No investing activity in this period." />
          <ActivitySection title="Financing Activities" data={data.financing} colorClass="fn-fin" empty="No financing activity in this period." />

          <div className="section-card fn-summary">
            <div className="section-header">Summary</div>
            <FnRow label="Opening Cash Balance"       value={data.opening_cash} />
            <FnRow label="(+) Net from Operating"     value={data.operating?.net} />
            <FnRow label="(+) Net from Investing"     value={data.investing?.net} />
            <FnRow label="(+) Net from Financing"     value={data.financing?.net} />
            <FnRow label="Net Change in Cash"         value={data.net_change} bold />
            <FnRow label="CLOSING CASH BALANCE"       value={data.closing_cash} bold big />
          </div>
        </>
      )}
    </div>
  );
}

function ActivitySection({ title, data, colorClass, empty }) {
  if (!data || data.lines.length === 0) {
    return (
      <div className={`section-card ${colorClass}`}>
        <div className="section-header">{title}</div>
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
          {empty || 'No activity'}
        </div>
      </div>
    );
  }
  return (
    <div className={`section-card ${colorClass}`}>
      <div className="section-header">{title}</div>
      {data.lines.map((l, i) => (
        <div key={i} className="fn-activity-row">
          <span>{l.label}</span>
          <span className="fn-activity-in">{l.in > 0 ? inr(l.in) : '—'}</span>
          <span className="fn-activity-out">{l.out > 0 ? inr(l.out) : '—'}</span>
          <span style={{ fontWeight: 700, color: l.net >= 0 ? 'var(--secondary)' : 'var(--danger)' }}>
            {inr(l.net)}
          </span>
        </div>
      ))}
      <div className="fn-activity-row fn-activity-total">
        <span>Net {title.split(' ')[0]} Cash Flow</span>
        <span className="fn-activity-in">{inr(data.total_in)}</span>
        <span className="fn-activity-out">{inr(data.total_out)}</span>
        <span>{inr(data.net)}</span>
      </div>
    </div>
  );
}

function FnRow({ label, value, bold, big }) {
  const v = Number(value || 0);
  return (
    <div className={`fn-row ${bold ? 'fn-bold' : ''} ${big ? 'fn-big' : ''}`}>
      <span>{label}</span>
      <span style={{ color: v < 0 ? 'var(--danger)' : 'inherit' }}>{inr(v)}</span>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   2. TRIAL BALANCE
═══════════════════════════════════════════════════════════ */
function TrialBalance() {
  const [asOf, setAsOf] = useState(today());
  const { data, loading, error, reload } = useApi(
    () => api.financialsPlus.trialBalance(asOf), [asOf]
  );

  return (
    <div className="fn-body">
      <div className="fn-filters">
        <div className="form-group">
          <label>As of</label>
          <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={reload}>Refresh</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading || !data ? <Spinner /> : (
        <>
          <div className="fn-kpis">
            <Kpi label="Total Debit"   value={inr(data.total_debit)}  tone="green" />
            <Kpi label="Total Credit"  value={inr(data.total_credit)} tone="orange" />
            <Kpi label="Balanced?"     value={data.balanced ? '✓ Yes' : '✗ No'}
                 tone={data.balanced ? 'green' : 'red'} />
          </div>

          <div className="section-card" style={{ overflowX: 'auto' }}>
            <div className="section-header">Trial Balance as of {data.as_of}</div>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Account Type</th>
                  <th>Dr Total</th>
                  <th>Cr Total</th>
                  <th>Net Dr</th>
                  <th>Net Cr</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map(r => (
                  <tr key={r.account_type}>
                    <td style={{ textAlign: 'left', fontWeight: 600 }}>{r.account_type}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{inr(r.debit)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{inr(r.credit)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--secondary)' }}>
                      {r.net_debit > 0 ? inr(r.net_debit) : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--warning)' }}>
                      {r.net_credit > 0 ? inr(r.net_credit) : '—'}
                    </td>
                  </tr>
                ))}
                <tr style={{ background: '#f5f9fc', fontWeight: 700, borderTop: '2px solid var(--primary)' }}>
                  <td style={{ textAlign: 'left' }}>TOTAL</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{inr(data.total_debit)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{inr(data.total_credit)}</td>
                  <td colSpan="2" style={{ color: data.balanced ? 'var(--secondary)' : 'var(--danger)' }}>
                    {data.balanced ? '✓ Books balance' : '⚠️ Books do not balance!'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   3. AGED RECEIVABLES
═══════════════════════════════════════════════════════════ */
function AgedReceivables() {
  const { data, loading, error, reload } = useApi(() => api.financialsPlus.agedReceivables(), []);

  return (
    <div className="fn-body">
      <div className="fn-filters">
        <div style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)' }}>
          Customer outstanding balances bucketed by age.
        </div>
        <button className="btn btn-primary btn-sm" onClick={reload}>Refresh</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading || !data ? <Spinner /> : <AgedTable data={data} kind="customer" />}
    </div>
  );
}

function AgedPayables() {
  const { data, loading, error, reload } = useApi(() => api.financialsPlus.agedPayables(), []);

  return (
    <div className="fn-body">
      <div className="fn-filters">
        <div style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)' }}>
          What you owe suppliers, bucketed by age.
        </div>
        <button className="btn btn-primary btn-sm" onClick={reload}>Refresh</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading || !data ? <Spinner /> : <AgedTable data={data} kind="supplier" />}
    </div>
  );
}

function AgedTable({ data, kind }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <>
      <div className="fn-kpis">
        <Kpi label="0–30 days"   value={inr(data.totals.current)} tone="green" />
        <Kpi label="31–60 days"  value={inr(data.totals.b_30)} />
        <Kpi label="61–90 days"  value={inr(data.totals.b_60)} tone="orange" />
        <Kpi label="91–180 days" value={inr(data.totals.b_90)} tone="red" />
        <Kpi label="180+ days"   value={inr(data.totals.over_90)} tone="red" />
        <Kpi label="TOTAL"       value={inr(data.totals.total)} tone="blue" />
      </div>

      <div className="section-card" style={{ overflowX: 'auto' }}>
        <div className="section-header">
          Aged {kind === 'customer' ? 'Receivables' : 'Payables'} as of {data.as_of}
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{kind === 'customer' ? 'Customer' : 'Supplier'}</th>
              <th>0–30</th>
              <th>31–60</th>
              <th>61–90</th>
              <th>91–180</th>
              <th>180+</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                No outstanding balances
              </td></tr>
            ) : (data.rows || []).map(r => (
              <React.Fragment key={kind === 'customer' ? r.customer_id : r.supplier_id}>
                <tr>
                  <td style={{ textAlign: 'left', fontWeight: 600 }}>{r.party_name}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{r.current > 0 ? inr(r.current) : '—'}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{r.b_30    > 0 ? inr(r.b_30)    : '—'}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--warning)' }}>{r.b_60    > 0 ? inr(r.b_60)    : '—'}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--danger)'  }}>{r.b_90    > 0 ? inr(r.b_90)    : '—'}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--danger)'  }}>{r.over_90 > 0 ? inr(r.over_90) : '—'}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{inr(r.total)}</td>
                  <td>
                    <button className="btn btn-sm"
                            onClick={() => toggle(kind === 'customer' ? r.customer_id : r.supplier_id)}>
                      {expanded[kind === 'customer' ? r.customer_id : r.supplier_id] ? '▲' : '▼'}
                    </button>
                  </td>
                </tr>
                {expanded[kind === 'customer' ? r.customer_id : r.supplier_id] && (
                  <tr>
                    <td colSpan="8" style={{ background: '#fafcff', padding: '8px 14px' }}>
                      <table style={{ width: '100%', fontSize: 12 }}>
                        <thead>
                          <tr style={{ color: 'var(--text-muted)' }}>
                            <th style={{ textAlign: 'left' }}>Bill</th>
                            <th style={{ textAlign: 'left' }}>Date</th>
                            <th>Age</th>
                            <th>Outstanding</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.bills.map((b, i) => (
                            <tr key={i}>
                              <td style={{ textAlign: 'left', fontFamily: 'monospace' }}>{b.bill_no}</td>
                              <td style={{ textAlign: 'left' }}>{b.bill_date}</td>
                              <td style={{ textAlign: 'center' }}>{b.age_days} days</td>
                              <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                                {inr(b.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}


/* ── Shared KPI tile ── */
function Kpi({ label, value, tone }) {
  return (
    <div className={`fn-kpi ${tone ? 'kpi-' + tone : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}
